import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { minute } from '@utils/milliseconds';

export const newDateIgnoreLocalTimezone = (date: string) => {
  if (!date) {
    return new Date();
  }

  const scheduledDate = new Date(date);
  const userTimezoneOffset = scheduledDate.getTimezoneOffset() * minute;

  return new Date(scheduledDate.getTime() + userTimezoneOffset);
};

const getLocalTimezoneOffset = (): string => {
  const date = new Date();

  const offsetInMinutes = date.getTimezoneOffset();
  const sign = offsetInMinutes > 0 ? '-' : '+';

  const offsetInHours = Math.floor(Math.abs(offsetInMinutes / 60));
  const offsetMinutes = Math.abs(offsetInMinutes % 60);

  const formattedOffsetHours = String(offsetInHours).padStart(2, '0');
  const formattedOffsetMinutes = String(offsetMinutes).padStart(2, '0');

  return `GMT${sign}${formattedOffsetHours}:${formattedOffsetMinutes}`;
};

function getTimeInLocalTimezone(hour: string, minute: string, journeyTimezoneOffset: string) {
  if (!hour || !minute) {
    return;
  }

  const hours = parseInt(hour, 10);
  const minutes = parseInt(minute, 10);
  const journeyTimezoneOffsetInMinutes = getOffsetInMinutes(journeyTimezoneOffset);

  const date = new Date();
  const localOffsetInMinutes = date.getTimezoneOffset();

  const offsetDifferenceInMinutes = journeyTimezoneOffsetInMinutes + localOffsetInMinutes;

  const utcTime = new Date(Date.UTC(0, 0, 0, hours, minutes));
  utcTime.setMinutes(utcTime.getMinutes() - offsetDifferenceInMinutes);

  const localHours = utcTime.getUTCHours().toString().padStart(2, '0');
  const localMinutes = utcTime.getUTCMinutes().toString().padStart(2, '0');

  return `${localHours}:${localMinutes}`;
}

function getOffsetInMinutes(offset: string) {
  const sign = offset.startsWith('GMT-') ? -1 : 1;
  const hours = parseInt(offset.substring(4, 6), 10);
  const minutes = parseInt(offset.substring(7, 9), 10);
  return sign * (hours * 60 + minutes);
}

const getLocalTimezoneTooltip = (hour: string, minute: string, journeyTimezoneOffset: string) => {
  if (!hour || !minute || !journeyTimezoneOffset) {
    return '';
  }

  const localOffset = getLocalTimezoneOffset();
  const localTime = getTimeInLocalTimezone(hour, minute, journeyTimezoneOffset);

  return `${localTime} (${localOffset})`;
};

type ActionTimeWithTimezoneTooltipProps = {
  actionText: string;
  hour: string;
  minute: string;
  journeyTimezoneOffset: string;
};

export const ActionTimeWithTimezoneTooltip = ({
  actionText,
  hour,
  minute,
  journeyTimezoneOffset,
}: ActionTimeWithTimezoneTooltipProps) => {
  const { t } = useScopedTranslate(locale);

  if (!Boolean(actionText)) {
    return <Text font="regular14" color="purple50" text={t('startMissing')} />;
  }

  const localTimezoneTime = getLocalTimezoneTooltip(hour, minute, journeyTimezoneOffset);
  const paddedHour = hour?.padStart(2, '0');
  const paddedMinute = minute?.padStart(2, '0');

  return (
    <Text
      font="regular14"
      text={
        <>
          {actionText}
          <Text
            font="regular14"
            style={{ display: 'inline-block' }}
            text={` (${journeyTimezoneOffset})`}
            tooltip={t('timezoneTooltip', {
              action: `${paddedHour}:${paddedMinute} (${journeyTimezoneOffset})`,
              computer: localTimezoneTime,
            })}
          />
        </>
      }
    />
  );
};

const locale = {
  en: {
    timezoneTooltip: 'Action • {action}\nYour computer • {computer}',
    startMissing: 'Start date missing',
  },
};
